'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackMarketing/useTrackMarketing';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { useRef } from 'react';
import { abbreviateString } from '../../utils/abbreviateString';
import type { RelatedContentItemType } from './types';

/**
 * RelatedContentItem component that renders a single content card with image, headline, and description.
 *
 * Features:
 * - Displays a teaser image with configurable dimensions
 * - Shows headline (truncated to 40 characters) and description (truncated to 109 characters)
 * - Includes marketing tracking for click analytics
 * - Uses Card UI component with responsive min-height
 * - Entire card is clickable through an overlaid link
 *
 * @param props - The component props
 * @param props.headline - The main title of the content item
 * @param props.description - Brief description of the content
 * @param props.path - URL path for navigation when card is clicked
 * @param props.teaser_image - Image object containing url, alt text, title, width, and height
 * @param props.taxonomy_terms - Array of taxonomy terms used for campaign tracking
 * @returns A clickable content card component
 */
export const RelatedContentItem = ({
  headline,
  description,
  path,
  teaser_image,
  taxonomy_terms,
}: RelatedContentItemType) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { trackClick } = useTrackMarketing(
    {
      category: 'related_content_cards',
      campaignType: 'content',
      label: 'Content-Beratung',
      campaignId: taxonomy_terms?.join(', '),
      trackingTarget: path,
    },
    cardRef,
    path,
  );
  return (
    <Card className="relative m-0.5 min-h-[23rem]" ref={cardRef}>
      <CardHeader className="p-0">
        {teaser_image?.url && (
          <Image
            src={teaser_image.url}
            alt={teaser_image.alt || teaser_image.title || 'Teaser Image'}
            width={teaser_image.width || 100}
            height={teaser_image.height || 100}
            className="rounded-t"
          />
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-1.5">
        <Link
          href={path}
          passHref
          className="text-size-headline-xl block after:absolute after:top-0 after:left-0 after:size-full"
          onClick={() => trackClick()}
        >
          {abbreviateString(headline, 40)}
        </Link>
        <div className="text-size-body-sm">{abbreviateString(description, 109)}</div>
      </CardContent>
    </Card>
  );
};
